import { useEffect, useRef, useState } from "react";
import axios from "axios";
import Nav from "../components/Nav";
import Hero from "../components/Hero";
import Manifesto from "../components/Manifesto";
import EventsPortfolio from "../components/EventsPortfolio";
import Creed from "../components/Creed";
import FlagLights from "../components/FlagLights";
import Rounds from "../components/Rounds";
import { useRounds } from "../hooks/useRounds";
import { useIsPhone } from "../hooks/useIsPhone";
import Footer from "../components/Footer";
import { useReveals } from "../motion/reveals";
import { ScrollTrigger } from "gsap/ScrollTrigger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GM_FILM = {
  desktop: { src: "/video/hero/gm-hero-r5-desktop.mp4", poster: "/video/hero/gm-hero-r5-desktop.jpg" },
  // Phone (client, 2026-09-19): 30s of the Yas Round 6 reel (0:00-0:30), native 9:16.
  phone: { src: "/video/hero/gm-hero-phone-r6.mp4", poster: "/video/hero/gm-hero-phone-r6.jpg" },
};

export default function Landing() {
  // `version` no longer drives the landing hero (locked per device, 2026-09-21).
  const navLogoRef = useRef(null);
  const navWordmarkRef = useRef(null);
  const navMorphRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState([]);
  const { rounds } = useRounds(2026);
  // Client, 2026-09-17: phones get a static nav lockup (no wordmark morph),
  // portrait hero frames and no ticker. Nav + Hero remount on the switch so
  // the morph refs are created or torn down cleanly.
  const phone = useIsPhone();

  useEffect(() => {
    (async () => {
      try {
        const [e, s] = await Promise.all([
          axios.get(`${API}/events`),
          axios.get(`${API}/stats`),
        ]);
        setEvents(e.data || []);
        setStats(s.data || []);
      } catch (err) {
        // graceful fallback with hardcoded shapes so UI still renders
        console.warn("api fetch failed", err?.message);
        setEvents([
          {
            slug: "ultimate-lap",
            name: "Ultimate Lap",
            tagline: "Yas Marina and Dubai Autodrome. Six rounds. One lap counts.",
            subtitle: "Time attack series",
            status: "active",
            lockup: "/brand/logos/GM-Ultimate-Lap-white-480.webp",
            accent: "#FFDD00",
          },
          {
            slug: "speed-gates",
            name: "Speed Gates",
            tagline: "Cones, gates, one clean run.",
            subtitle: "Autocross",
            status: "coming-soon",
            lockup: "/brand/logos/GM-Speed-Gates-white-480.webp",
            accent: "#FF7F00",
          },
          {
            slug: "track-days",
            name: "Track Days",
            tagline: "Your car. Our circuit. Quality drivers, zero chaos.",
            subtitle: "Quality drivers, zero chaos",
            status: "coming-soon",
            lockup: "/brand/logos/GM-Logo-white-480.webp",
            accent: "#FFFFFF",
          },
          {
            slug: "corporate-events",
            name: "Corporate Events",
            tagline: "Motorsport as hospitality.",
            subtitle: "Your brand, flawlessly delivered",
            status: "coming-soon",
            lockup: "/brand/logos/GM-Logo-white-480.webp",
            accent: "#FFFFFF",
          },
        ]);
        setStats([
          { key: "seasons", label: "Seasons run", value: 4 },
          { key: "events", label: "Events staged", value: 47 },
          { key: "drivers", label: "Drivers on the grid", value: 312 },
          { key: "records", label: "Lap records set", value: 18 },
        ]);
      }
    })();
  }, []);

  useReveals([events, stats, rounds]);

  useEffect(() => {
    const t = setTimeout(() => ScrollTrigger.refresh(), 500);
    return () => clearTimeout(t);
  }, [events, stats, rounds]);

  return (
    <>
      {/* Client, 2026-09-17: no Rulebook button in the landing header (any viewport). */}
      <Nav
        key={phone ? "nav-phone" : "nav-desktop"}
        morph={!phone}
        onLogoRef={(el) => (navLogoRef.current = el)}
        onWordmarkRef={(el) => (navWordmarkRef.current = el)}
        onMorphRef={(el) => (navMorphRef.current = el)}
      />
      <main data-testid="landing-main">
        {/* Client decision (2026-09-21): the landing hero is LOCKED per device -
            desktop = V2 photo rotation, phones = V1 portrait film. The V1/V2
            toggle no longer drives this hero (it still drives the others until
            the client decides on them). No CTA for now (client, 2026-09-21). */}
        <Hero
          key={phone ? "hero-phone" : "hero-desktop"}
          morph={!phone}
          phone={phone}
          film={phone ? GM_FILM.phone : undefined}
          title=""
          ctas={[]}
          navLogoRef={navLogoRef}
          navWordmarkRef={navWordmarkRef}
          navMorphRef={navMorphRef}
        />
        <Manifesto stats={stats} />
        <EventsPortfolio events={events} />
        <Creed />
        {/* Client, 2026-10-10: the scrolling text marquee is gone; a yellow-flag
            "sector boards" light band sits in the same slot and geometry.
            Shown on phones too (client, later same day) - CSS trims it to 5 boards there. */}
        <FlagLights testid="race-ticker" />
        <Rounds rounds={rounds} />
      </main>
      <Footer partners={false} />
    </>
  );
}
